/**
 * @class Order
 */
export default class Order {
    /**
     * @param {Number}  id
     * @param {String}  identifier
     * @param {Number}  status
     * @param {String}  databaseName
     * @param {String}  productName
     * @param {String}  productDisplayName
     * @param {String}  fandeckName
     * @param {String}  fandeckDisplayName
     * @param {String}  colorName
     * @param {String}  colorDisplayName
     * @param {String}  colorRgb
     * @param {String}  baseName
     * @param {String}  baseDisplayName
     * @param {String}  packageName
     * @param {String}  packageDisplayName
     * @param {String}  bippDisplayName
     * @param {Number}  dispensedPackageCount
     * @param {Number}  packageCount
     * @param {Number}  nominalAmount
     * @param {Number}  unit
     * @param {String}  created
     * @param {String}  customerName
     * @param {Boolean} canAcquire
     * @param {String}  clientName
     * @param {Array}   customPackageName
     */
    constructor(
        id,
        identifier,
        status,
        databaseName,
        productName,
        productDisplayName,
        fandeckName,
        fandeckDisplayName,
        colorName,
        colorDisplayName,
        colorRgb,
        baseName,
        baseDisplayName,
        packageName,
        packageDisplayName,
        bippDisplayName,
        dispensedPackageCount,
        packageCount,
        nominalAmount,
        unit,
        created,
        customerName,
        canAcquire,
        clientName,
        customPackageName = null,
        colorCode = null
    ) {
        this._id = id;
        this._identifier = identifier;
        this._status = status;
        this._databaseName = databaseName;
        this._productName = productName;
        this._productDisplayName = productDisplayName;
        this._fandeckName = fandeckName;
        this._fandeckDisplayName = fandeckDisplayName;
        this._colorName = colorName;
        this._colorDisplayName = colorDisplayName;
        this._colorRgb = colorRgb;
        this._baseName = baseName;
        this._baseDisplayName = baseDisplayName;
        this._packageName = packageName;
        this._packageDisplayName = packageDisplayName;
        this._bippDisplayName = bippDisplayName;
        this._dispensedPackageCount = dispensedPackageCount;
        this._packageCount = packageCount;
        this._nominalAmount = nominalAmount;
        this._unit = unit;
        this._created = new Date(created);
        this._customerName = customerName;
        this._productAlternativeName = null;
        this._colorAlternativeName = null;
        this._fandeckAlternativeName = null;
        this._note = null;
        this._moreInfoInternal = null;
        this._canAcquire = canAcquire;
        this._clientName = clientName;
        this._customPackageName = customPackageName;
        this._colorCode = colorCode;

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - Order");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get identifier() {
        return this._identifier;
    }

    /**
     * @param {String} identifier
     */
    set identifier(identifier) {
        this._identifier = identifier;
    }

    /**
     * @return {Number}
     */
    get status() {
        return this._status;
    }

    /**
     * @param {Number} status
     */
    set status(status) {
        this._status = status;
    }

    /**
     * @return {String}
     */
    get databaseName() {
        return this._databaseName;
    }

    /**
     * @param {String} databaseName
     */
    set databaseName(databaseName) {
        this._databaseName = databaseName;
    }

    /**
     * @return {String}
     */
    get productName() {
        return this._productName;
    }

    /**
     * @param {String} productName
     */
    set productName(productName) {
        this._productName = productName;
    }

    /**
     * @return {String}
     */
    get productDisplayName() {
        return this._productDisplayName;
    }

    /**
     * @param {String} productDisplayName
     */
    set productDisplayName(productDisplayName) {
        this._productDisplayName = productDisplayName;
    }

    /**
     * @return {String}
     */
    get fandeckName() {
        return this._fandeckName;
    }

    /**
     * @param {String} fandeckName
     */
    set fandeckName(fandeckName) {
        this._fandeckName = fandeckName;
    }

    /**
     * @return {String}
     */
    get fandeckDisplayName() {
        return this._fandeckDisplayName;
    }

    /**
     * @param {String} fandeckDisplayName
     */
    set fandeckDisplayName(fandeckDisplayName) {
        this._fandeckDisplayName = fandeckDisplayName;
    }

    /**
     * @return {String}
     */
    get colorName() {
        return this._colorName;
    }

    /**
     * @param {String} colorName
     */
    set colorName(colorName) {
        this._colorName = colorName;
    }

    /**
     * @return {String}
     */
    get colorDisplayName() {
        return this._colorDisplayName;
    }

    /**
     * @param {String} colorDisplayName
     */
    set colorDisplayName(colorDisplayName) {
        this._colorDisplayName = colorDisplayName;
    }

    /**
     * @return {String}
     */
    get baseName() {
        return this._baseName;
    }

    /**
     * @param {String} baseName
     */
    set baseName(baseName) {
        this._baseName = baseName;
    }

    /**
     * @return {String}
     */
    get baseDisplayName() {
        return this._baseDisplayName;
    }

    /**
     * @param {String} baseDisplayName
     */
    set baseDisplayName(baseDisplayName) {
        this._baseDisplayName = baseDisplayName;
    }

    /**
     * @return {String}
     */
    get colorRgb() {
        return this._colorRgb;
    }

    /**
     * @param {String} colorRgb
     */
    set colorRgb(colorRgb) {
        this._colorRgb = colorRgb;
    }

    /**
     * @return {String}
     */
    get packageName() {
        return this._packageName;
    }

    /**
     * @param {String} packageName
     */
    set packageName(packageName) {
        this._packageName = packageName;
    }

    /**
     * @return {String}
     */
    get packageDisplayName() {
        return this._packageDisplayName;
    }

    /**
     * @param {String} packageDisplayName
     */
    set packageDisplayName(packageDisplayName) {
        this._packageDisplayName = packageDisplayName;
    }

    /**
     * @return {String}
     */
    get bippDisplayName() {
        return this._bippDisplayName;
    }

    /**
     * @param {String} bippDisplayName
     */
    set bippDisplayName(bippDisplayName) {
        this._bippDisplayName = bippDisplayName;
    }

    /**
     * @return {Number}
     */
    get dispensedPackageCount() {
        return this._dispensedPackageCount;
    }

    /**
     * @param {Number} dispensedPackageCount
     */
    set dispensedPackageCount(dispensedPackageCount) {
        this._dispensedPackageCount = dispensedPackageCount;
    }

    /**
     * @return {Number}
     */
    get packageCount() {
        return this._packageCount;
    }

    /**
     * @param {Number} packageCount
     */
    set packageCount(packageCount) {
        this._packageCount = packageCount;
    }

    /**
     * @return {Number}
     */
    get nominalAmount() {
        return this._nominalAmount;
    }

    /**
     * @param {Number} nominalAmount
     */
    set nominalAmount(nominalAmount) {
        this._nominalAmount = nominalAmount;
    }

    /**
     * @return {Number}
     */
    get unit() {
        return this._unit;
    }

    /**
     * @param {Number} unit
     */
    set unit(unit) {
        this._unit = unit;
    }

    /**
     * @return {Object}
     */
    get created() {
        return this._created;
    }

    /**
     * @param {Object} created
     */
    set created(created) {
        this._created = created;
    }

    /**
     * @return {String}
     */
    get customerName() {
        return this._customerName;
    }

    /**
     * @param {String} customerName
     */
    set customerName(customerName) {
        this._customerName = customerName;
    }

    /**
     * @return {String}
     */
    get productAlternativeName() {
        return this._productAlternativeName;
    }

    /**
     * @param {String} prodcutNameAlternative
     */
    set productAlternativeName(productAlternativeName) {
        this._productAlternativeName = productAlternativeName;
    }

    /**
     * @return {String}
     */
    get colorAlternativeName() {
        return this._colorAlternativeName;
    }

    /**
     * @param {String} colorNameAlternative
     */
    set colorAlternativeName(colorAlternativeName) {
        this._colorAlternativeName = colorAlternativeName;
    }

    /**
     * @return {String}
     */
    get fandeckAlternativeName() {
        return this._fandeckAlternativeName;
    }

    /**
     * @param {String} fandeckAlternativeName
     */
    set fandeckAlternativeName(fandeckAlternativeName) {
        this._fandeckAlternativeName = fandeckAlternativeName;
    }

    /**
     * @return {String}
     */
    get note() {
        return this._note;
    }

    /**
     * @param {String} note
     */
    set note(note) {
        this._note = note;
    }

    /**
     * @return {String}
     */
    get moreInfoInternal() {
        return this._moreInfoInternal;
    }

    /**
     * @param {String} moreInfoInternal
     */
    set moreInfoInternal(moreInfoInternal) {
        this._moreInfoInternal = moreInfoInternal;
    }

    /**
     * @return {Boolean}
     */
    get canAcquire() {
        return this._canAcquire;
    }

    /**
     * @param {Boolean} canAcquire
     */
    set canAcquire(canAcquire) {
        this._canAcquire = canAcquire;
    }

    /**
     * @return {String}
     */
    get clientName() {
        return this._clientName;
    }

    /**
     * @param {String} clientName
     */
    set clientName(clientName) {
        this._clientName = clientName;
    }

    /**
     * @return {Array}
     */
    get customPackageName() {
        return this._customPackageName;
    }

    /**
     * @param {Array} customPackageName
     */
    set customPackageName(customPackageName) {
        this._customPackageName = customPackageName;
    }

    /**
     * @return {String}
     */
    get colorCode() {
        return this._colorCode;
    }

    /**
     * @param {String} colorCode
     */
    set colorCode(colorCode) {
        this._colorCode = colorCode;
    }

    /**
     * @return {Object}
     */
    clone() {
        let clone = new Order(
            this._id,
            this._identifier,
            this._status,
            this._databaseName,
            this._productName,
            this._productDisplayName,
            this._fandeckName,
            this._fandeckDisplayName,
            this._colorName,
            this._colorDisplayName,
            this._colorRgb,
            this._baseName,
            this._baseDisplayName,
            this._packageName,
            this._packageDisplayName,
            this._bippDisplayName,
            this._dispensedPackageCount,
            this._packageCount,
            this._nominalAmount,
            this._unit,
            this._created,
            this._customerName,
            this._canAcquire,
            this._clientName,
            this._customPackageName,
            this._colorCode
        );

        clone.productAlternativeName = this._productAlternativeName;
        clone.colorAlternativeName = this._colorAlternativeName;
        clone.fandeckAlternativeName = this._fandeckAlternativeName;
        clone.note = this._note;
        clone.moreInfoInternal = this._moreInfoInternal;

        return clone;
    }

    /**
     * Clones Order object and updates status and date property.
     *
     * @param   {Number}    status
     * @param   {Timestamp} updated
     * @return  {Object}
     */
    updateOrderStatus = (status, updated) => {
        let order = this.clone();

        order.status = status;
        order.created = new Date(updated);

        return order;
    };

    /**
     * @return {String}
     */
    toString() {
        return "Order (" + this._id + ")";
    }
}
